import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiBriefcase, FiHash, FiLayers, FiFileText } from 'react-icons/fi';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const font = "'Outfit', sans-serif";
const BL = '#6366f1';
const C = { text: '#1e1b4b', sub: '#6b7280', border: '#e0e7ff', bg: '#eef2ff', white: '#fff', light: '#a5b4fc' };

const InfoRow = ({ icon, label, value }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: 'rgba(99,102,241,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BL, fontSize: 14 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.light, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, wordBreak: 'break-word' }}>{value || '—'}</div>
        </div>
    </div>
);

const TeacherProfile = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        api.get('/api/teacher/dashboard')
            .then(r => setStats(r.data.stats))
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    }, []);

    const initials = (user?.name || 'T').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

    return (
        <div style={{ width: '100%', fontFamily: font }}>
            {/* Hero Card */}
            <div style={{ background: 'linear-gradient(135deg,#818cf8 0%,#6366f1 60%,#4f46e5 100%)', borderRadius: 20, padding: window.innerWidth <= 768 ? '20px' : '28px 24px 24px', marginBottom: 18, color: '#fff', display: 'flex', alignItems: window.innerWidth <= 768 ? 'center' : 'center', flexDirection: window.innerWidth <= 768 ? 'column' : 'row', gap: 20, flexWrap: 'wrap', boxShadow: '0 6px 24px rgba(99,102,241,0.25)', textAlign: window.innerWidth <= 768 ? 'center' : 'left' }}>
                <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 900, flexShrink: 0, border: '3px solid rgba(255,255,255,0.4)', margin: window.innerWidth <= 768 ? '0 auto' : 0 }}>
                    {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <h1 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 4px', letterSpacing: -0.4 }}>{user?.name || 'Teacher'}</h1>
                    <p style={{ fontSize: 13, opacity: 0.85, margin: '0 0 10px' }}>
                        {user?.designation || 'Faculty'}{user?.department ? ` · ${user.department}` : ''}
                    </p>
                    <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 20, background: 'rgba(255,255,255,0.18)', fontSize: 11, fontWeight: 700 }}>🎓 Teacher</span>
                </div>
            </div>

            {/* Stat Pills */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 18 }}>
                {[
                    { label: 'Assignments', value: stats?.totalAssignments ?? '—', icon: <FiLayers size={14} />, color: BL },
                    { label: 'Papers', value: stats?.totalPapers ?? '—', icon: <FiFileText size={14} />, color: '#8b5cf6' },
                    { label: 'Approved', value: stats?.approved ?? '—', icon: <FiHash size={14} />, color: '#10b981' },
                ].map(s => (
                    <div key={s.label} style={{ borderRadius: 14, background: s.color + '12', border: `1.5px solid ${s.color}30`, padding: '14px 10px', textAlign: 'center' }}>
                        <div style={{ color: s.color, display: 'flex', justifyContent: 'center', marginBottom: 4 }}>{s.icon}</div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{loading ? '…' : s.value}</div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Info Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
                {[
                    {
                        title: 'Personal Info',
                        rows: [
                            { icon: <FiUser />, label: 'Full Name', value: user?.name },
                            { icon: <FiMail />, label: 'Email', value: user?.email },
                            { icon: <FiHash />, label: 'Username', value: user?.username },
                        ],
                    },
                    {
                        title: 'Academic Info',
                        rows: [
                            { icon: <FiBriefcase />, label: 'Designation', value: user?.designation },
                            { icon: <FiLayers />, label: 'Department', value: user?.department },
                            { icon: <FiHash />, label: 'Teacher ID', value: user?.teacher_id },
                        ],
                    },
                ].map(section => (
                    <div key={section.title} style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 3px 12px rgba(99,102,241,0.07)' }}>
                        <div style={{ padding: '12px 16px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 4, height: 16, borderRadius: 3, background: 'linear-gradient(180deg,#818cf8,#6366f1)', display: 'inline-block' }} />
                            {section.title}
                        </div>
                        {section.rows.map((r, i) => <InfoRow key={i} {...r} />)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TeacherProfile;
